/**
 * The API client.
 *
 * **Types are generated** from `backend/openapi.json` into `src/types/generated.ts`, and CI
 * fails when the committed spec and the application disagree. That is what stops the
 * client drifting: without it the frontend keeps compiling against an endpoint the
 * backend no longer has, and the failure appears in front of an analyst.
 *
 * The transport is hand-written and small. A generated fetch layer would also generate
 * the parts this application needs to control — the access-token refresh, the correlation
 * header, and the deliberate refusal to follow a redirect to another origin.
 */

/** Where the API lives. Same origin: the CSP is `default-src 'none'`. */
const BASE = '/api/v1';

/** The header the backend correlates a request with its audit entry through. */
export const CORRELATION_HEADER = 'X-Correlation-Id';

/** An error carrying the status and the backend's problem detail. */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(status: number, detail: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }

  /**
   * The `code` member of the RFC 7807 problem document, when the body carried one.
   *
   * **Match on this, not on the status.** The login screen tested for HTTP 428 while the
   * API answers 401 with `MFA_REQUIRED`, so the second-factor step never appeared and
   * anybody who enrolled could not sign in again through the browser. A status number is
   * a shared assumption held in two places; the code is part of the published contract
   * and is asserted by the contract suite.
   */
  get code(): string | null {
    const body = this.detail;
    if (body !== null && typeof body === 'object' && 'code' in body) {
      const value = body.code;
      return typeof value === 'string' ? value : null;
    }
    return null;
  }
}

/** Held in memory only. A token in `localStorage` is a token an XSS reads. */
let accessToken: string | null = null;

/** Record the access token for subsequent requests. */
export function setAccessToken(token: string | null): void {
  accessToken = token;
  // Receiving a token — from login, MFA, or the mount refresh — is a fresh refresh: the
  // proactive slide below measures from here so an active session's window keeps moving.
  if (token !== null) lastRefreshAt = Date.now();
}

/** Whether a token is currently held. */
export function hasAccessToken(): boolean {
  return accessToken !== null;
}

/**
 * Called when the session cannot be recovered, so the shell can show the login screen.
 *
 * A module-level hook rather than a thrown error the caller must interpret: any request
 * anywhere can be the one that discovers the session is gone, and every call site
 * handling that individually is how one of them ends up not handling it.
 */
let onAuthLost: (() => void) | null = null;

/**
 * Register what happens when the session is lost for good.
 *
 * @param handler - Called once per lost session, or `null` to unregister.
 */
export function setAuthLostHandler(handler: (() => void) | null): void {
  onAuthLost = handler;
}

/**
 * Session idle tracking.
 *
 * **A session must end after real inactivity, and the reason it did not is here.** The
 * refresh below is driven by any request that gets a 401 — including the background
 * pollers that fire every minute with no user in the room — so an open tab kept renewing
 * itself forever. The fix is to make "activity" mean *user* activity: the window records
 * genuine input, and a refresh is refused once the user has been idle past the window the
 * server configured (`idle_timeout_seconds` on the session). The server enforces the same
 * bound by letting the refresh token lapse; this is the half that stops the client from
 * silently renewing through an idle period.
 *
 * `lastActivityAt` is shared across tabs through a `BroadcastChannel`, so a person working
 * in one tab does not get signed out of another — any tab's input keeps them all alive.
 * Deliberately *not* `localStorage`: session state there is within reach of any XSS, which
 * is exactly what this application refuses to do with the access token.
 */
let lastActivityAt = Date.now();
let idleWindowMs = 0; // 0 disables the check — set from the session, cleared on sign-out.
let lastActivityBroadcastAt = 0;

/**
 * When the session last obtained a fresh token, and how stale it may get under activity.
 *
 * **This is the half that was missing, and the reason active sessions timed out early.**
 * The refresh window slides only when a refresh happens, and refreshes were driven by
 * access-token expiry — not by the user. So the "idle timeout" was really "time until the
 * token from the last refresh expires", which collapses to seconds if the user goes quiet
 * late in that token's life. The client now refreshes *proactively* while the user is
 * active, keeping the token no more than `proactiveRefreshMs` stale, so the server's
 * (slightly longer) refresh token never lapses before the idle timer fires.
 */
let lastRefreshAt = Date.now();
let proactiveRefreshMs = 0; // 0 disables proactive refresh — derived from the idle window.

const activityChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('prp.activity') : null;

/** Note that the user did something just now, and tell the other tabs. */
export function markActivity(): void {
  const now = Date.now();
  lastActivityAt = now;
  // Throttled: input fires continuously, but other tabs only need a coarse heartbeat.
  if (activityChannel && now - lastActivityBroadcastAt > 2_000) {
    lastActivityBroadcastAt = now;
    activityChannel.postMessage(now);
  }
}

/**
 * Configure the idle window, in milliseconds. Zero disables idle enforcement.
 *
 * @param ms - The window, typically `session.idle_timeout_seconds * 1000`.
 */
export function setIdleWindowMs(ms: number): void {
  idleWindowMs = Math.max(0, ms);
  // Slide the window forward on activity by refreshing once the token is ~a third of the
  // idle window old. That keeps a refresh well inside the server token's margin (the idle
  // window plus one access lifetime), so an active session is never lapsed server-side.
  proactiveRefreshMs = idleWindowMs > 0 ? idleWindowMs * 0.3 : 0;
  if (idleWindowMs > 0) {
    lastActivityAt = Date.now();
    lastRefreshAt = Date.now();
  }
}

/** Whether the user has been idle past the configured window. */
export function isSessionIdle(): boolean {
  return idleWindowMs > 0 && Date.now() - lastActivityAt >= idleWindowMs;
}

/**
 * Whether an active session's token is stale enough to slide the window forward.
 *
 * True only when idle enforcement is on, the user is *not* idle, there has been activity
 * since the last refresh, and the token is older than the proactive window. The
 * activity-since-last-refresh guard is what stops this from renewing an idle session: a
 * session with no input just ages out, exactly as intended.
 */
export function shouldProactivelyRefresh(): boolean {
  if (proactiveRefreshMs <= 0 || isSessionIdle()) return false;
  if (lastActivityAt <= lastRefreshAt) return false;
  return Date.now() - lastRefreshAt >= proactiveRefreshMs;
}

/**
 * Refresh the access token ahead of time if an active session's window needs sliding.
 *
 * Gated on tab visibility so only the focused tab does it: two tabs refreshing at once
 * would each present the shared refresh cookie, and the second — arriving with a token the
 * first has already rotated — would trip reuse detection and sign the user out of both.
 */
export async function maybeProactiveRefresh(): Promise<void> {
  const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
  if (visible && shouldProactivelyRefresh()) await refreshAccessToken();
}

if (activityChannel) {
  // Another tab reported activity: adopt it if it is newer, so shared input keeps this
  // tab's session alive too.
  activityChannel.addEventListener('message', (event: MessageEvent) => {
    const other = Number(event.data);
    if (!Number.isNaN(other) && other > lastActivityAt) lastActivityAt = other;
  });
}

if (typeof window !== 'undefined') {
  const bump = (): void => markActivity();
  // `passive` because none of these are cancelled, and `pointermove` is high-frequency.
  for (const event of ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart', 'wheel']) {
    window.addEventListener(event, bump, { passive: true });
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') bump();
  });
}

/**
 * The refresh currently in flight, shared by every request that hit a 401.
 *
 * **Single-flight is not an optimisation here, it is correctness.** Refresh tokens
 * rotate, and presenting a consumed one revokes the entire family — that is the defence
 * against a stolen token being replayed. A page with six queries open would fire six
 * refreshes on expiry, five of which arrive with a token the first has already spent, and
 * the server would correctly treat that as theft and sign the user out of everything.
 */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchange the refresh cookie for a new access token, at most once at a time.
 *
 * @returns Whether a new token was obtained.
 */
async function refreshAccessToken(): Promise<boolean> {
  // **Do not renew through an idle period.** A refresh here is what a background poll's
  // 401 would trigger; refusing it once the user is idle past the window is what turns an
  // idle-but-open tab into a signed-out one instead of an eternally-renewing one. The
  // server also refuses — its refresh token has lapsed — but declining here means the
  // session ends promptly rather than one poll-and-401 later.
  if (isSessionIdle()) return false;
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(new URL(`${BASE}/auth/refresh`, window.location.origin), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        redirect: 'error',
        credentials: 'same-origin',
      });
      if (!response.ok) return false;
      const session = (await response.json()) as { access_token?: string };
      if (!session.access_token) return false;
      accessToken = session.access_token;
      // The window has just slid forward: the server rotated the refresh cookie and reset
      // its lifetime, so record when, for the proactive check to measure from.
      lastRefreshAt = Date.now();
      return true;
    } catch {
      return false;
    } finally {
      // Cleared in a microtask rather than immediately: callers awaiting this promise
      // must all observe the same result before a later 401 can start a second one.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();
  return refreshInFlight;
}

/**
 * Paths that must never trigger a refresh-and-retry.
 *
 * `login` and `mfa/verify` answer 401 to mean "those credentials are wrong", and
 * `refresh` answering 401 means the session is over. Retrying any of them would turn a
 * clear refusal into a loop.
 */
const NO_RETRY = ['/auth/login', '/auth/refresh', '/auth/mfa/verify', '/auth/logout'];

/**
 * What one request carries.
 *
 * **`body` is `never` until a caller names its type**, and that is the whole point of the
 * generic. It was `unknown`, which meant no request body in the application was checked
 * against the API at all — and the plugin credential form sent `credential_value` where
 * the schema says `value` for as long as the form had existed, rejected every time, with
 * `tsc` perfectly happy. The generated types described the request correctly throughout;
 * nothing was asking them.
 *
 * So a body now has to be declared: `request<Response, RequestBody>(…)`. Passing one
 * without naming its type fails to compile, which is the property that keeps the next
 * field name honest.
 */
interface RequestOptions<B = never> {
  method?: string;
  body?: B;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

/**
 * Perform one API request.
 *
 * @param path - Path below `/api/v1`.
 * @param options - Method, body and query.
 * @returns The parsed response body.
 * @throws ApiError - On any non-2xx response.
 *
 * @typeParam T - What the endpoint returns.
 * @typeParam B - What the request body is, from `@/lib/schemas`. Defaults to `never`, so
 *   a call sending a body without naming its type does not compile.
 */
export async function request<T, B = never>(
  path: string,
  options: RequestOptions<B> = {},
  retried = false,
): Promise<T> {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  // Compared without a query string, so `/auth/login?x=1` cannot slip past the no-retry
  // list by carrying one.
  const pathOnly = path.split('?')[0];
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    // Never follow a redirect. The API does not issue one, so a redirect means
    // something is in the path that should not be — and following it would send the
    // Authorization header somewhere nobody chose.
    redirect: 'error',
    credentials: 'same-origin',
    signal: options.signal ?? null,
  });

  // **The access token lives fifteen minutes and nothing renewed it.** Every session died
  // mid-click at the quarter hour with "Authentication failed", because the only refresh
  // in the application ran once when the page loaded. A 401 here means the token aged
  // out, not that the user is unwelcome: exchange the refresh cookie and replay once.
  if (response.status === 401 && !retried && !NO_RETRY.some((path) => path === pathOnly)) {
    if (await refreshAccessToken()) {
      return request<T, B>(path, options, true);
    }
    // The refresh cookie is gone or spent. The session is genuinely over, and the shell
    // needs to know rather than every screen discovering it separately.
    accessToken = null;
    onAuthLost?.();
  }

  if (response.status === 204) return null as T;

  const text = await response.text();
  const parsed: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, parsed, describe(response.status, parsed));
  }
  return parsed as T;
}

/**
 * Fetch a file from the API and hand it to the browser as a download.
 *
 * **This exists because a plain download anchor cannot authenticate.** The access
 * token is held in memory by design — a token in `localStorage` is a token an XSS reads —
 * so an anchor navigation and a bare `fetch` both arrive with no credentials at all. The
 * refresh cookie does not cover for it either: it is scoped to `/api/v1/auth`, so the
 * browser never sends it to a report or an evidence path.
 *
 * Both download controls did exactly that and had never worked. The reports button
 * reported the 401 as "REFUSED — This report cannot be issued.", which reads as the export
 * gate declining rather than as a request that forgot to say who it was. The evidence
 * anchor was quieter and worse: no error path at all, so the browser saved the JSON error
 * body under the name of the file the analyst wanted.
 *
 * The object URL is built from a blob retyped to `application/octet-stream` regardless of
 * what the server said. Evidence is whatever a source returned, up to and including HTML,
 * and the one thing this platform will not do is hand a browser something it might render.
 * The `download` attribute alone would be enough; the retype means it is enough twice.
 *
 * @param path - Path below `/api/v1`.
 * @param filename - Name to save under.
 * @throws ApiError - On any non-2xx response, carrying the problem document so the caller
 *     can show the real reason.
 */
export async function download(path: string, filename: string): Promise<void> {
  const response = await fetch(new URL(`${BASE}${path}`, window.location.origin), {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    redirect: 'error',
    credentials: 'same-origin',
  });

  if (!response.ok) {
    const text = await response.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      // A non-JSON body from a proxy or a gateway. `describe` falls back on the status.
    }
    throw new ApiError(response.status, parsed, describe(response.status, parsed));
  }

  const blob = new Blob([await response.blob()], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
  } finally {
    // Deferred: revoking synchronously can cancel the download in some browsers, which
    // is a failure that only shows up on a real machine.
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  }
}

/**
 * Upload multipart form data to the API.
 *
 * **Content-Type is deliberately not set.** The browser has to generate it, because a
 * multipart body is only parseable with the boundary token the browser chose — setting
 * `multipart/form-data` by hand omits the boundary and the server rejects the body as
 * malformed. This is the one request shape where leaving the header alone is correct.
 *
 * Like `download`, this exists because the bearer token lives in memory: a bare `fetch`
 * or a plain `<form action>` would arrive with no credentials at all.
 *
 * @param path - Path below `/api/v1`.
 * @param form - The form data to send.
 * @returns The parsed response body.
 * @throws ApiError - On any non-2xx response.
 */
export async function upload<T>(path: string, form: FormData): Promise<T> {
  const response = await fetch(new URL(`${BASE}${path}`, window.location.origin), {
    method: 'POST',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    body: form,
    redirect: 'error',
    credentials: 'same-origin',
  });

  const text = await response.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    // A non-JSON body from a proxy. `describe` falls back on the status.
  }

  if (!response.ok) {
    throw new ApiError(response.status, parsed, describe(response.status, parsed));
  }
  return parsed as T;
}


/**
 * Turn a failure into something an analyst can act on.
 *
 * The backend's problem detail is preferred; the fallbacks name the likely cause rather
 * than the status code, because "403" tells a reviewer nothing about which permission
 * they are missing.
 */
function describe(status: number, detail: unknown): string {
  if (detail && typeof detail === 'object' && 'detail' in detail) {
    const { detail: value } = detail;
    if (typeof value === 'string') return value;
  }
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 403) return 'Your role does not permit this action.';
  if (status === 409) return 'This has already been decided.';
  if (status === 503) return 'A dependency is unavailable. Try again shortly.';
  return `Request failed (${status}).`;
}
